// src/views/ImportExport/DatosExportadosProani/ListarDatosExportadosProani.jsx
import React, { useEffect, useState } from "react";
import IconEye from "../../../components/icons/IconEye";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import FiltroView from "./FiltroDatosExportadosProani";
import VisualizarView from "./VerDatosExportadosProani";
import CrearView from "./ListarLineasNoExportadasAlSap";
import DynamicTable from "../../../components/listing/DynamicTable";
//Debug
import DebugDimensions from "../../../components/debug/DebugDimensions";

// ============================
// CONFIG
// ============================
const DEFAULT_PAGE_SIZE = 30;

// ============================
// COMPONENTE
// ============================
export default function ListarDatosExportadosProani() {
  const [items, setItems] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1); // UI 1-based
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);
  const [cargando, setCargando] = useState(false);

  // ============================
  // FILTROS
  // ============================
  const [filtros, setFiltros] = useState({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

  const limpiarSeleccion = () => {
    setFiltros({});
    setPaginaActual(1);
    fetchConsulta(1, porPagina, {});
  };

  // ============================
  // MODAL VER
  // ============================
  const [idSeleccionado, setIdSeleccionado] = useState(null);
  useEscClose(() => setIdSeleccionado(null), idSeleccionado != null);

  // CREAR
  const [showCrear, setShowCrear] = useState(false);
  useEscClose(() => setShowCrear(false), showCrear);

  // ============================
  // FETCH
  // ============================
  const fetchConsulta = async (
    page = paginaActual,
    size = porPagina,
    filtrosAplicados = filtros,
  ) => {
    setCargando(true);

    try {
      const url = apiRoutes.proaniOut.listUrl({
        page: page - 1, // backend 0-based
        size,
        ...filtrosAplicados,
      });

      const data = await appFetchJson(url);

      setItems(data.items || []);
      setTotalBackend(data.totalElements || 0);
    } catch (e) {
      console.error("Error cargando datos ProaniOut", e);
      setItems([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  // ============================
  // INIT
  // ============================
  useEffect(() => {
    fetchConsulta(1, porPagina, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================
  // PAGINACIÓN
  // ============================
  const totalPaginas = Math.max(1, Math.ceil(totalBackend / porPagina));

  const cambiarPagina = (num) => {
    const pagina = Math.min(Math.max(num, 1), totalPaginas);
    setPaginaActual(pagina);
    fetchConsulta(pagina, porPagina);
  };

  // ============================
  // DEFINICIÓN DE COLUMNAS (RESPONSIVE)
  // ============================
  const columns = [
    {
      header: "Fecha",
      accessor: "dataFecha",
      type: "center",
      width: "12%",
      render: (row) => row.dataFecha ?? "—",
    },
    {
      header: "Código Tipo",
      accessor: "codigoTipo",
      type: "code", // centrado y monoespaciado
      width: "12%",
    },
    {
      header: "Nombre de Archivo",
      accessor: "nombreArchivo",
      type: "text",
      width: "20%",
      cellClass: "whitespace-normal", // opcional
      render: (row) => (
        <div style={{ wordBreak: "break-all", overflowWrap: "break-word" }}>
          {row.nombreArchivo}
        </div>
      ),
    },
    {
      header: "Línea de Texto",
      accessor: "textoLinea",
      type: "text",
      width: "30%",
      cellClass: "whitespace-normal", // opcional
      render: (row) => (
        <div style={{ wordBreak: "break-all", overflowWrap: "break-word" }}>
          {row.textoLinea}
        </div>
      ),
    },
    {
      header: "Procesado",
      accessor: "codigoProcesadoLabel",
      type: "center",
      width: "12%",
    },
    {
      header: "Acción",
      type: "action",
      width: "14%",
      render: (row) => (
        <div className="flex justify-center items-center gap-1">
          <button
            className="p-1"
            title="Ver"
            onClick={() => setIdSeleccionado(row.id)}
          >
            <IconEye />
          </button>
        </div>
      ),
    },
  ];

  // ============================
  // RENDER
  // ============================
  return (
    <div className="page">
      <ListHeader
        title="Datos Exportados PROANI"
        onFilterClick={() => setMostrarFiltros(true)}
        filters={filtros}
        onClearFilters={limpiarSeleccion}
        showAdd
        addLabel="Listar lineas no exportadas al SAP"
        onAddClick={() => setShowCrear(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPaginaActual(1);
          fetchConsulta(1, size, filtros);
        }}
      />
      {mostrarFiltros && (
        <FiltroView
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            setPaginaActual(1);
            setMostrarFiltros(false);
            fetchConsulta(1, porPagina, nuevosFiltros);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}
      {/* CREAR */}
      {showCrear && (
        <CrearView
          onClose={() => setShowCrear(false)}
          onSaved={() => {
            setShowCrear(false);
            fetchConsulta(1, porPagina, filtros);
          }}
        />
      )}

      {/* Ver/Visualizar */}
      {idSeleccionado && (
        <VisualizarView
          id={idSeleccionado}
          onClose={() => setIdSeleccionado(null)}
        />
      )}

      {/* Indicador de carga */}
      {cargando && (
        <div className="mt-2 text-sm text-gray-600">Cargando datos…</div>
      )}
      {/* Tabla responsiva */}
      {!cargando && (
        <DynamicTable
          size="M" // 6 columnas → tamaño estándar (cambia en lg)
          data={items}
          columns={columns}
        />
      )}
      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />
      {/* Debug */}
      {process.env.NODE_ENV === "development" && <DebugDimensions />}
    </div>
  );
}
